import { useState } from 'react'
import { Calendar as CalendarIcon } from 'lucide-react'
import { format } from 'date-fns'
import { ar, fr } from 'date-fns/locale'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

interface DateSelectorProps {
  date: Date
  onDateChange: (date: Date) => void
  label?: string
}

export function DateSelector({ date, onDateChange, label }: DateSelectorProps) {
  const { t, i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const locale = i18n.language === 'ar' ? ar : fr
  const isRTL = i18n.language === 'ar'

  const handleDateSelect = (selectedDate: Date | undefined) => {
    if (selectedDate) {
      // Create a new date at midnight local time to avoid timezone issues
      const localDate = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate(),
        0,
        0,
        0,
        0
      )
      onDateChange(localDate)
      setOpen(false)
    }
  }

  return (
    <div className="space-y-2">
      {label && <label className="text-sm font-medium">{label}</label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              'w-[240px] justify-start text-left font-normal',
              !date && 'text-muted-foreground'
            )}
          >
            <CalendarIcon className={cn("h-4 w-4", isRTL ? "ml-2" : "mr-2")} />
            {date ? (
              format(date, 'PPP', { locale })
            ) : (
              <span>{t('analytics.selectDate')}</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={date}
            onSelect={handleDateSelect}
            initialFocus
            locale={locale}
            dir={isRTL ? 'rtl' : 'ltr'}
            weekStartsOn={isRTL ? 6 : 1}
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}
